import React, { useState, useContext, useRef } from "react";
import apiClient from "../../utils/apiClient";
import { useNavigate } from "react-router-dom";
import userAvatar from "../../assets/icons/user-avatar.jpg";
import "./Profile.css";
import EditProfileModal from "./EditProfileModal";
import TokenService from "../../utils/tokenService";
import ConfirmationModal from "../../components/UI/ConfirmationModal";
import { ProfileContext } from "../../contexts/ProfileContext";

const Profile = () => {
  const navigate = useNavigate();
  const { profile, setProfile, refreshProfile } = useContext(ProfileContext);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  const handleLogout = async () => {
    try {
      const refreshToken = TokenService.getRefreshToken();
      if (refreshToken) {
        await apiClient.post("/signout", { RefreshToken: refreshToken });
      }
      TokenService.clearTokens();
      navigate("/");
    } catch (err) {
      TokenService.clearTokens();
      navigate("/");
    }
  };

  const handleDeleteAccount = async () => {
    try {
      if (!profile?.UserId) {
        throw new Error("No UserId found on profile.");
      }
      await apiClient.delete(`/members/${profile.UserId}`);
      TokenService.clearTokens();
      navigate("/");
    } catch (err) {
      setErrorMessage("Failed to delete account. Please try again.");
      setShowErrorModal(true);
    }
  };

  const handleSaveProfile = async (updatedName, updatedPhone) => {
    try {
      await apiClient.put("/myadminprofile", {
        Name: updatedName,
        Phone: updatedPhone,
      });
      setProfile((prev) => ({
        ...prev,
        Name: updatedName,
        Phone: updatedPhone,
      }));
      setShowEditModal(false);
    } catch (err) {
      setErrorMessage("Error updating profile. Check console for details.");
      setShowErrorModal(true);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please upload an image file.");
      setShowErrorModal(true);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader.result.split(",")[1];
      handleChangePhoto(base64Data);
    };
    reader.readAsDataURL(file);
  };

  const handleChangePhoto = async (base64Image) => {
    try {
      await apiClient.put("/myadminprofile", {
        ...profile,
        Picture: base64Image,
      });
    } catch (err) {
      setErrorMessage("Failed to update profile picture. Please try again.");
      return setShowErrorModal(true);
    }

    try { 
      await refreshProfile();
    } catch (err) {
      setErrorMessage("Picture uploaded, but refresh failed:", err);
      return setShowErrorModal(true);
    }
  };

  if (!profile) {
    return <div className="profile-container">Loading...</div>;
  }

  if (error) {
    return <div className="profile-container">Error: {error}</div>;
  }

  return (
    <div className="profile-container">
      <div className="left-section">
        <div className="left-content">
          <img
            src={profile.Picture || userAvatar}
            alt="Profile"
            className="profile-image"
          />
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileInputChange}
          />
          <button
            className="change-photo-btn"
            onClick={() => fileInputRef.current.click()}
          >
            Change Photo
          </button>
        </div>
      </div>

      <div className="right-section">
        <div className="right-content">
          <h2 className="account-title">Profile</h2>
          <div className="user-info">
            <div className="info-row">
              <span className="info-label">Full Name</span>
              <span className="info-value">{profile.Name}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Phone No.</span>
              <span className="info-value">{profile.Phone}</span>
            </div>
          </div>

          <div className="button-group">
            <button className="edit-btn" onClick={() => setShowEditModal(true)}>
              Edit
            </button>

            <button
              className="logout-btn"
              onClick={() => setShowLogoutConfirm(true)}
            >
              Logout
            </button>
          </div>

          <div className="delete-account">
            <button
              className="delete-btn"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {showEditModal && (
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveProfile}
        />
      )}

      <ConfirmationModal
        isOpen={showLogoutConfirm}
        message="Are you sure you want to logout?"
        confirmLabel="Logout"
        cancelLabel="Cancel"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        message="Are you sure you want to delete your account? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDanger={true}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDeleteAccount();
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {showErrorModal && (
        <ConfirmationModal
          isOpen={showErrorModal}
          message={errorMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
          onConfirm={() => setShowErrorModal(false)}
        />
      )}
    </div>
  );
};

export default Profile;
